import CloseRounded from "@mui/icons-material/CloseRounded";
import { Box, IconButton, Modal, Paper, Typography } from "@mui/material";
import type { MouseEvent as ReactMouseEvent } from "react";

export interface ScreenLocationPreviewModalProps {
	open: boolean;
	onClose: () => void;
	/** Image URL for the location preview (`previewUrl` from the screen location record). */
	previewUrl: string;
	/** Shown at the bottom-left of the white card (e.g. tab / screen name). */
	locationName: string;
	/** Added to `theme.zIndex.modal` when this must sit above another dialog. Default 10. */
	stackingOffset?: number;
}

/**
 * Full-viewport overlay with a dimmed backdrop and a centered white card.
 * Dismiss by clicking the dimmed backdrop (outside the white card) or Escape. The image uses most of the card; the location name sits on the bottom-left of the card.
 * Pattern aligned with
 * [Future Designs — location preview](https://www.figma.com/design/WaTHJQV96FYmvZfV4w63Zm/Future-Designs?node-id=8291-122855&m=dev).
 */
const ScreenLocationPreviewModal = ({
	open,
	onClose,
	previewUrl,
	locationName,
	stackingOffset = 10,
}: ScreenLocationPreviewModalProps) => {
	/** Full-screen layer stacks above the backdrop, so backdrop never receives clicks — treat direct hits on this layer as “outside” the card. */
	const handleOverlayClick = (event: ReactMouseEvent<HTMLDivElement>) => {
		if (event.target === event.currentTarget) {
			onClose();
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			slotProps={{
				backdrop: {
					sx: {
						backgroundColor: "rgba(15, 15, 15, 0.55)",
						backdropFilter: "blur(2px)",
					},
				},
			}}
			sx={{
				zIndex: (theme) => theme.zIndex.modal + stackingOffset,
			}}
		>
			<Box
				role="presentation"
				onClick={handleOverlayClick}
				sx={{
					position: "fixed",
					inset: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: { xs: 1.5, sm: 3 },
					outline: "none",
				}}
			>
				<Paper
					elevation={12}
					sx={{
						position: "relative",
						display: "flex",
						flexDirection: "column",
						width: "100%",
						maxWidth: "min(965px, 92vw)",
						height: "clamp(280px, min(88vh, 720px), 720px)",
						borderRadius: 2,
						overflow: "hidden",
						bgcolor: "background.paper",
					}}
				>
					<IconButton
						aria-label="Close preview"
						onClick={onClose}
						size="small"
						sx={{
							position: "absolute",
							top: 8,
							right: 8,
							zIndex: 1,
							color: "rgba(13, 31, 48, 0.7)",
							backgroundColor: "rgba(255, 255, 255, 0.92)",
							boxShadow: "0 1px 4px rgba(13, 31, 48, 0.15)",
							"&:hover": { backgroundColor: "#FFFFFF", color: "rgba(13, 31, 48, 0.95)" },
						}}
					>
						<CloseRounded fontSize="small" />
					</IconButton>

					<Box
						sx={{
							flex: "1 1 0",
							minHeight: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: "grey.100",
							px: 1.5,
							py: 1,
						}}
					>
						{previewUrl ? (
							<Box
								component="img"
								src={previewUrl}
								alt=""
								sx={{
									width: "100%",
									height: "100%",
									maxHeight: "100%",
									objectFit: "contain",
									display: "block",
								}}
							/>
						) : null}
					</Box>

					<Box
						sx={{
							flexShrink: 0,
							px: 2,
							py: 1.75,
							pt: 1.25,
						}}
					>
						<Typography
							component="p"
							sx={{
								fontWeight: 500,
								fontSize: "10px",
								lineHeight: "14px",
								letterSpacing: 0,
								color: "text.primary",
								m: 0,
								textAlign: "left",
							}}
						>
							{locationName}
						</Typography>
					</Box>
				</Paper>
			</Box>
		</Modal>
	);
};

export default ScreenLocationPreviewModal;
